import { useEffect, useRef } from "react";
import type { RoomView } from "@grundbesitz/shared";
import { isSpectating } from "../../lib/gameView";
import { HELP_KEY, TAB_HOTKEYS, hotkeyAction, isTypingTarget } from "../../lib/hotkeys";
import { useAct } from "./hooks";
import { useUiStore } from "./uiStore";
import type { GameTab } from "./uiStore";

/**
 * Keyboard shortcuts of the game screen. They stay quiet while the player types or a dialog is open,
 * the dialog owns the keyboard then. Mount once.
 */
export function useHotkeys(room: RoomView, tabs: GameTab[]): void {
  const { run } = useAct();
  const openTab = useUiStore((s) => s.openTab);
  const setHotkeyHelp = useUiStore((s) => s.setHotkeyHelp);
  const roomRef = useRef(room);
  roomRef.current = room;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const key = e.key.toLowerCase();

      if (key === HELP_KEY) {
        e.preventDefault();
        setHotkeyHelp(true);
        return;
      }
      const tab = TAB_HOTKEYS.find((t) => t.key === key);
      if (tab) {
        if (!tabsRef.current.includes(tab.tab)) return;
        e.preventDefault();
        openTab(tab.tab);
        return;
      }
      // Space on a focused button or field presses that one.
      if (key === " " && e.target instanceof Element && e.target.closest('button, a[href], [role="button"]')) return;

      const current = roomRef.current;
      if (!current.game || isSpectating(current)) return;
      const action = hotkeyAction(current.game, current.you.playerId, key);
      if (!action) return;
      e.preventDefault();
      void run(action);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [run, openTab, setHotkeyHelp]);
}
